import { DependencyContainer } from '../../../DependencyContainer';
import {
  AutonomousMonitorEmailOutreach,
  ConfigurationCTAUrlResponse,
  ConfigurationEmailPayload,
  ConfigurationEmailResponse,
  MessageTypes,
  OutreachConfigResponse,
  OutreachConfigResponseWebhook,
  OutreachSendTestSupportMessagePayload,
  OutreachSubscriberConfigResponse,
  SaveOutreachConfigDto,
  SaveOutreachWebhookConfig,
} from '../types';
import { OutreachTypes } from '../../../urls';

export default class OutreachService {
  constructor(private readonly factory: DependencyContainer) {}

  async saveOutreachEmailConfig(
    partnerId: string,
    monitorId: string,
    payload: SaveOutreachConfigDto,
  ): Promise<void> {
    await this.factory.outreachClient.saveOutreachEmailConfig(
      partnerId,
      monitorId,
      payload,
    );
  }

  async saveOutreachWebhookConfig(
    partnerId: string,
    monitorId: string,
    payload: SaveOutreachWebhookConfig,
  ): Promise<void> {
    await this.factory.outreachClient.saveOutreachWebhookConfig(
      partnerId,
      monitorId,
      payload,
    );
  }

  async saveOutreachPushConfig(
    partnerId: string,
    monitorId: string,
    payload: SaveOutreachConfigDto,
  ): Promise<void> {
    await this.factory.outreachClient.saveOutreachPushConfig(
      partnerId,
      monitorId,
      payload,
    );
  }

  async updateOutreachEmailConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
    payload: SaveOutreachConfigDto,
  ): Promise<void> {
    await this.factory.outreachClient.updateOutreachEmailConfig(
      partnerId,
      monitorId,
      outreachId,
      payload,
    );
  }

  async updateOutreachWebhookConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
    payload: SaveOutreachConfigDto,
  ): Promise<void> {
    await this.factory.outreachClient.updateOutreachWebhookConfig(
      partnerId,
      monitorId,
      outreachId,
      payload,
    );
  }

  async updateOutreachPushConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
    payload: SaveOutreachConfigDto,
  ): Promise<void> {
    await this.factory.outreachClient.updateOutreachPushConfig(
      partnerId,
      monitorId,
      outreachId,
      payload,
    );
  }

  async deleteOutreachEmailConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
  ): Promise<void> {
    await this.factory.outreachClient.deleteOutreachEmailConfig(
      partnerId,
      monitorId,
      outreachId,
    );
  }

  async deleteOutreachWebhookConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
  ): Promise<void> {
    await this.factory.outreachClient.deleteOutreachWebhookConfig(
      partnerId,
      monitorId,
      outreachId,
    );
  }

  async deleteOutreachPushConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
  ): Promise<void> {
    await this.factory.outreachClient.deleteOutreachPushConfig(
      partnerId,
      monitorId,
      outreachId,
    );
  }

  async retrieveOutreachEmailConfig(
    partnerId: string,
    monitorId: string,
  ): Promise<OutreachConfigResponse[]> {
    const response = await this.factory.outreachClient.retrieveOutreachEmailConfig(
      partnerId,
      monitorId,
    );
    return response;
  }

  async retrieveOutreachWebhookConfig(
    partnerId: string,
    monitorId: string,
  ): Promise<OutreachConfigResponseWebhook[]> {
    const response = await this.factory.outreachClient.retrieveOutreachWebhookConfig(
      partnerId,
      monitorId,
    );
    return response;
  }

  async retrieveOutreachPushConfig(
    partnerId: string,
    monitorId: string,
  ): Promise<OutreachSubscriberConfigResponse[]> {
    const response = await this.factory.outreachClient.retrieveOutreachPushConfig(
      partnerId,
      monitorId,
    );
    return response;
  }

  async saveEmails(
    partnerId: string,
    emails: string[],
  ): Promise<ConfigurationEmailResponse[]> {
    const promises = emails.map(async (email: string) => {
      return await this.factory.outreachClient.saveEmail(partnerId, email);
    });
    return Promise.all(promises);
  }

  async saveEmail(
    partnerId: string,
    email: ConfigurationEmailPayload,
  ): Promise<ConfigurationEmailResponse> {
    return await this.factory.outreachClient.saveEmailWithName(
      partnerId,
      email,
    );
  }

  async updateEmail(
    partnerId: string,
    email: ConfigurationEmailResponse,
  ): Promise<ConfigurationEmailResponse> {
    return await this.factory.outreachClient.updateEmail(partnerId, email);
  }

  async deleteEmail(
    partnerId: string,
    emailId: string,
  ): Promise<ConfigurationEmailResponse> {
    return await this.factory.outreachClient.deleteEmail(partnerId, emailId);
  }

  async getEmails(partnerId: string): Promise<ConfigurationEmailResponse[]> {
    return await this.factory.outreachClient.retrieveEmails(partnerId);
  }

  async saveCTAUrl(
    partnerId: string,
    url: string,
  ): Promise<ConfigurationCTAUrlResponse> {
    return await this.factory.outreachClient.saveCTAUrl(partnerId, url);
  }

  async updateCTAUrl(
    partnerId: string,
    url: ConfigurationCTAUrlResponse,
  ): Promise<ConfigurationCTAUrlResponse> {
    return await this.factory.outreachClient.updateCTAUrl(partnerId, url);
  }

  async deleteCTAUrl(
    partnerId: string,
    urlId: string,
  ): Promise<ConfigurationCTAUrlResponse> {
    return await this.factory.outreachClient.deleteCTAUrl(partnerId, urlId);
  }

  async getCTAUrls(partnerId: string): Promise<ConfigurationCTAUrlResponse[]> {
    return await this.factory.outreachClient.retrieveCTAUrls(partnerId);
  }

  async sendTestMessage<T extends MessageTypes>(
    partnerId: string,
    monitorId: string,
    payload: OutreachSendTestSupportMessagePayload<T>,
    messageType: MessageTypes,
    outreachType: OutreachTypes = OutreachTypes.Email,
  ): Promise<any> {
    return await this.factory.outreachClient.sendTestMessage<T>(
      partnerId,
      monitorId,
      payload,
      messageType,
      outreachType,
    );
  }

  async getAutonomousMonitorEmailOutreach(
    partnerId: string,
    monitorId: string,
  ) {
    const response = await this.factory.outreachClient.getAutonomousMonitorEmailOutreach(
      partnerId,
      monitorId,
    );
    return response.data.at(0);
  }

  async patchAutonomousMonitorEmailOutreach(
    partnerId: string,
    monitorId: string,
    payload: AutonomousMonitorEmailOutreach,
  ) {
    return await this.factory.outreachClient.patchAutonomousMonitorEmailOutreach(
      partnerId,
      monitorId,
      payload,
    );
  }

  async testAutonomousMonitorEmailOutreach(data: {
    partnerId: string;
    monitorId: string;
    recipients: string[];
    language?: string;
  }) {
    return await this.factory.outreachClient.testAutonomousMonitorEmailOutreach(
      data,
    );
  }
}
